import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Mic, ExternalLink, Calendar } from "lucide-react";

const SHOW_URL = "https://open.spotify.com/show/3eHlxijuL70U3ihV5PVajR";

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
};

const fmtDuration = (ms) => {
    if (!ms) return "";
    const m = Math.round(ms / 60000);
    return `${m} min`;
};

const EpisodeCard = ({ ep }) => (
    <div className="bg-paper rounded-2xl border border-border-line soft-shadow-sm overflow-hidden fade-up" data-testid={`episode-${ep.id}`}>
        <div className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-heading text-lg leading-tight text-ink line-clamp-2">{ep.name}</h3>
                <a href={ep.external_url} target="_blank" rel="noreferrer" className="text-soft hover:text-bordeaux p-1 shrink-0" title="Apri su Spotify">
                    <ExternalLink size={14} />
                </a>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-soft mb-3">
                {ep.release_date && <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(ep.release_date)}</span>}
                {ep.duration_ms && <span>· {fmtDuration(ep.duration_ms)}</span>}
            </div>
            <iframe
                title={ep.name}
                src={ep.embed_url}
                width="100%"
                height="152"
                frameBorder="0"
                allowtransparency="true"
                allow="encrypted-media"
                loading="lazy"
                className="rounded-lg"
            />
        </div>
    </div>
);

const Podcast = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/podcast/episodes?limit=24");
                setData(data);
            } catch {}
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div data-testid="podcast-page">
            {/* Hero banner with set image */}
            <section className="relative rounded-3xl overflow-hidden mb-10 soft-shadow border border-border-line min-h-[280px] sm:min-h-0 sm:aspect-[21/9]">
                <img
                    src="/foto-interviste.png"
                    alt="Set podcast Voci di Imprese"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: '50% 85%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10" />
                <div className="relative z-10 flex flex-col justify-end p-5 sm:p-8 md:p-12 text-white min-h-[280px] sm:min-h-0 sm:h-full">
                    <div className="text-[10px] sm:text-xs tracking-[0.3em] uppercase opacity-80">La voce del network</div>
                    <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl lg:text-6xl mt-2 max-w-2xl leading-tight">Ascolta chi c'è dietro l'impresa.</h1>
                    <p className="text-xs sm:text-sm md:text-base mt-2 sm:mt-3 max-w-xl opacity-90">Tutte le interviste dello show Voci di Imprese, direttamente da Spotify.</p>
                </div>
            </section>

            {loading && <div className="text-center py-10 text-muted">Caricamento episodi…</div>}

            {!loading && (!data?.configured || (data?.episodes || []).length === 0) && (
                /* Fallback: show full Spotify show embed */
                <section className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-4 sm:p-5 mb-10 overflow-hidden">
                    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-bordeaux/10 flex items-center justify-center shrink-0"><Mic size={14} className="text-bordeaux" /></div>
                            <h2 className="font-heading text-xl sm:text-2xl text-ink truncate">Voci di Imprese — su Spotify</h2>
                        </div>
                        <a href={SHOW_URL} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="open-spotify">
                            Apri su Spotify <ExternalLink size={12} />
                        </a>
                    </div>
                    <div className="w-full overflow-hidden rounded-xl">
                        <iframe
                            data-testid="spotify-show-embed"
                            title="Spotify show Voci di Imprese"
                            src={data?.embed_url || `https://open.spotify.com/embed/show/3eHlxijuL70U3ihV5PVajR`}
                            width="100%"
                            height="500"
                            frameBorder="0"
                            allowtransparency="true"
                            allow="encrypted-media"
                            loading="lazy"
                            style={{ width: "100%", maxWidth: "100%", display: "block" }}
                        />
                    </div>
                    {!data?.configured && (
                        <p className="text-[11px] text-soft mt-3 italic">
                            Per visualizzare gli episodi come card singole con il proprio player, configura <code>SPOTIFY_CLIENT_ID</code> e <code>SPOTIFY_CLIENT_SECRET</code> nelle variabili d'ambiente del backend.
                        </p>
                    )}
                </section>
            )}

            {!loading && data?.configured && (data?.episodes || []).length > 0 && (
                <section>
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-heading text-2xl text-ink">{data.episodes.length} episodi</h2>
                        <a href={SHOW_URL} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="open-spotify">
                            Tutti gli episodi su Spotify <ExternalLink size={12} />
                        </a>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {data.episodes.map((ep) => <EpisodeCard key={ep.id} ep={ep} />)}
                    </div>
                </section>
            )}
        </div>
    );
};

export default Podcast;
